import Markdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import { normalizeCourseNotation } from "./Math";

/**
 * Markdown enriquecido con matemática, compartido por todos los átomos que
 * llevan prosa.
 *
 * Existe porque el agente escribe markdown y LaTeX de forma natural en
 * cualquier campo de texto —el cuerpo de un `callout`, la explicación de un
 * `quiz`— y no solo en `prose`. Cuando cada átomo interpretaba su texto a su
 * manera, el mismo `$k_d$` se veía como fórmula en un sitio y como texto crudo
 * en otro. Aquí se renderiza una sola vez, igual para todos.
 *
 * También aplica la notación del curso al LaTeX (`normalizeCourseNotation`),
 * para que `$WACC$` inline no se componga como W·A·C·C mientras el mismo LaTeX
 * dentro de un átomo `formula` se lee correctamente.
 */

interface HastNode {
  type: string;
  tagName?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
  value?: string;
}

function classNameList(node: HastNode): string[] {
  const raw = node.properties?.className;
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === "string") return raw.split(/\s+/);
  return [];
}

function isMathElement(node: HastNode): boolean {
  const classes = classNameList(node);
  return classes.includes("math-inline") || classes.includes("math-display");
}

/**
 * Reescribe el LaTeX que `remark-math` dejó en el árbol, antes de que
 * `rehype-katex` lo consuma. El orden de `rehypePlugins` importa.
 */
function rehypeCourseNotation() {
  return (tree: HastNode): void => {
    const walk = (node: HastNode): void => {
      if (!node.children) return;
      if (node.tagName && isMathElement(node)) {
        for (const child of node.children) {
          if (child.type === "text" && typeof child.value === "string") {
            child.value = normalizeCourseNotation(child.value);
          }
        }
      }
      for (const child of node.children) walk(child);
    };
    walk(tree);
  };
}

const components: Components = {
  // Un enlace de cita no navega: señala la fuente.
  a({ href, children, ...rest }) {
    const isCitation = typeof href === "string" && /^(#src-|source:)/.test(href);
    if (isCitation) {
      return (
        <a href={href} data-citation="" {...rest}>
          {children}
        </a>
      );
    }
    return (
      <a href={href} target="_blank" rel="noreferrer noopener" {...rest}>
        {children}
      </a>
    );
  },
  // Las tablas anchas hacen scroll dentro de su propio carril, no en la página.
  table({ children, ...rest }) {
    return (
      <div className="md__table-scroll">
        <table {...rest}>{children}</table>
      </div>
    );
  },
};

export interface RichTextProps {
  children: string;
  className?: string;
}

export function RichText({ children, className }: RichTextProps) {
  return (
    <div className={["md", className].filter(Boolean).join(" ")}>
      <Markdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[
          rehypeCourseNotation,
          [rehypeKatex, { throwOnError: false, strict: false }],
        ]}
        components={components}
      >
        {children}
      </Markdown>
    </div>
  );
}

export default RichText;
